//Logo.tsx

import React from "react";
import { useConfig } from "../hooks/ConfigProvider"
import { getRemoteEnv } from "../config/remoteEnv";
import { useTheme } from "../hooks/ThemeContext";
import { ensureContrast } from "../utils/contrastColor";

type Props = {
    useCachedLogo?: boolean,
    fallbackName?: string,
    /** Overrides the text-fallback wordmark size (e.g. the menu footer). */
    wordmarkFontSize?: string,
}

// minimum contrast for the accent to be usable as the wordmark colour
const MIN_LOGO_CONTRAST = 3;

// Drop the old UNSCOPED cache keys once — they could carry another brand's
// logo/favicon on this shared origin.
try {
    ["cached-logo-url", "cached-logo-image", "cached-icon-url", "cached-icon-image"].forEach(k => localStorage.removeItem(k));
} catch { /* storage unavailable — nothing cached either */ }

const fetchAsBase64 = (url: string): Promise<string> => {
    return fetch(url)
        .then(res => res.blob())
        .then(blob => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        }));
}

const getCachedImage = (urlKey: string, imageKey: string): string | null => {
    try {
        return localStorage.getItem(imageKey);
    } catch {
        return null;
    }
}

const cacheImageForUrl = async (url: string, urlKey: string, imageKey: string): Promise<string> => {
    try {
        const base64 = await fetchAsBase64(url);
        try {
            localStorage.setItem(urlKey, url);
            localStorage.setItem(imageKey, base64);
        } catch {
            // Quota exceeded — fall back to caching just the URL
            try {
                localStorage.setItem(urlKey, url);
                localStorage.setItem(imageKey, url);
            } catch {
                // Give up silently
            }
        }
        return base64;
    } catch {
        // Fetch failed — cache the URL as fallback
        try {
            localStorage.setItem(urlKey, url);
            localStorage.setItem(imageKey, url);
        } catch {
            // Give up silently
        }
        return url;
    }
}

const resolveImage = async (url: string, urlKey: string, imageKey: string): Promise<string> => {
    const cachedUrl = localStorage.getItem(urlKey);
    if (cachedUrl === url) {
        // URL hasn't changed — use cached image if available
        const cachedImage = getCachedImage(urlKey, imageKey);
        if (cachedImage) return cachedImage;
    }
    // URL changed or no cache — fetch fresh and overwrite
    return cacheImageForUrl(url, urlKey, imageKey);
}

const setFavicon = (href: string) => {
    document
        .querySelectorAll("link[rel~='icon'], link[rel='shortcut icon']")
        .forEach(el => el.parentNode?.removeChild(el));

    const link = document.createElement("link");
    link.rel = "icon";
    link.href = href.startsWith("data:") ? href : `${href}?v=${Date.now()}`;
    document.head.appendChild(link);
}

const Logo = (props: Props) => {

    const [logoUrl, setLogoUrl] = React.useState("");
    const [triedResolve, setTriedResolve] = React.useState(false);
    const [imgFailed, setImgFailed] = React.useState(false);
    const { config } = useConfig();
    const { theme } = useTheme();

    // Every /r/{app} brand shares this origin's localStorage, so the cache
    // keys MUST be scoped per app — unscoped keys made one brand's logo (and
    // favicon) show up on another via the useCachedLogo path.
    const cacheScope = (() => {
        try { return getRemoteEnv().appId || "default"; } catch { return "default"; }
    })();
    const logoUrlKey   = `cached-logo-url:${cacheScope}`;
    const logoImageKey = `cached-logo-image:${cacheScope}`;
    const iconUrlKey   = `cached-icon-url:${cacheScope}`;
    const iconImageKey = `cached-icon-image:${cacheScope}`;

    React.useEffect(() => {
        setImgFailed(false);
        if (props.useCachedLogo) {
            try {
                setLogoUrl(localStorage.getItem(logoImageKey) || "");
            } catch {
                setLogoUrl("");
            }
            setTriedResolve(true);
            return;
        }
        const url = getRemoteEnv().logoUrl || (config && config?.properties?.logoUrl);
        if (!url) {
            // No logo for this brand — PURGE its scoped cache too. A stale
            // cross-brand config once poisoned this bucket with another
            // brand's image, and with no url of our own nothing would ever
            // overwrite it (the useCachedLogo consumers showed it forever).
            try {
                localStorage.removeItem(logoUrlKey);
                localStorage.removeItem(logoImageKey);
            } catch { /* storage unavailable */ }
            setLogoUrl("");
            setTriedResolve(true);
            return;
        }
        resolveImage(url, logoUrlKey, logoImageKey)
            .then(image => { setLogoUrl(image || ""); setTriedResolve(true); })
            .catch(() => { setLogoUrl(""); setTriedResolve(true); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, props.useCachedLogo, cacheScope])

    React.useEffect(() => {
        const appIconUrl = getRemoteEnv().appIconUrl;
        if (appIconUrl) {
            resolveImage(appIconUrl, iconUrlKey, iconImageKey)
                .then(image => setFavicon(image));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheScope])

    const t = theme as any;

    // Brand name for the text fallback — appName from remote config.
    const brandName: string =
        props.fallbackName ||
        getRemoteEnv().appName ||
        (config as any)?.properties?.name ||
        "";

    // Accent if it contrasts the top bar enough; otherwise a tuned version of
    // the accent (same hue, adjusted lightness) that does. Shared with the
    // top-bar nav via contrastColor.ts so the two stay identical.
    const accent: string =
        t.primaryAccentColor || t.categoryIndicatorColor || t.cartButtonColor || "";
    const topbarBg: string = t.topbarBackgroundColor || "";
    const wordmarkColor: string = ensureContrast(accent, topbarBg, MIN_LOGO_CONTRAST);

    const showImage = logoUrl !== "" && !imgFailed;

    if (showImage) {
        return (
            <img
                style={{ margin: 0 }}
                src={logoUrl}
                height="80%"
                alt={brandName || "logo"}
                onError={() => setImgFailed(true)}
            />
        );
    }

    if (triedResolve && brandName) {
        return (
            <span
                style={{
                    fontFamily: 'var(--app-display-font, "Bricolage Grotesque", sans-serif)',
                    fontWeight: 700,
                    fontSize: props.wordmarkFontSize || "clamp(18px, 2vw, 26px)",
                    letterSpacing: "0.01em",
                    lineHeight: 1,
                    margin: 0,
                    color: wordmarkColor,
                    whiteSpace: "nowrap",
                    marginLeft: "10px"
                }}
            >
                {brandName}
            </span>
        );
    }

    return <></>;
}

export default Logo;